// =============================================
// deadLinkDetector — read-only skan martwych linków wewnętrznych
// per opublikowany artykuł, klasyfikacja 3 typów.
// =============================================
//
// KRYTYCZNA REGUŁA (akcept. 2026-06-09):
// "Czy link jest żywy" sprawdzaj po ŻYWYM URL (wp_post_url path), NIE po slugu z DB.
// Powód: slug w DB bywa z polskimi znakami ("zmieniały-się"), wp_post_url jest
// ASCII ("zmienialy-sie"). Bez tego pominiemy rozjazdy gdzie link == slug z DB,
// ale różni się od żywego URL.
//
// liveSlugSet = path(wp_post_url) lowercase, bez protokołu/domeny/trailing slash.
// Tylko gdy decodedSlug ∈ liveSlugSet → SKIP (healthy).
//
// Dopiero gdy NIE w liveSlugSet → 3-step classify:
//   1. slugToNode (exact slug DB) → pending_target (no wp_post_url) lub slug_mismatch (ma wp_post_url, ale link != żywego URL)
//   2. slugifiedToNode (slugify(href) → match) → slug_mismatch / pending_target
//   3. fallback orphan matcher (jaccard slug+anchor vs candidates)
//
// Reuse: slugify (@/lib/slugify), findOrphanMatch (./orphanLinkMatcher)

import { supabase } from "@/integrations/supabase/client";
import { slugify } from "@/lib/slugify";
import {
  findOrphanMatch,
  type OrphanCandidate,
} from "./orphanLinkMatcher";

// Wide regex — accept POLSKIE znaki w href (slug w DB), nested elements w anchor
// (lazy match [\s\S]*?). Wykluczone absolute (https?://) — tylko relative.
const RELATIVE_LINK_RE =
  /<a\s+href="(\/[^"\s][^"]*)"\s*[^>]*>([\s\S]*?)<\/a>/gi;

export type DeadLinkType = "pending_target" | "slug_mismatch" | "orphan";
export type DeadLinkAction =
  | "replace_href"
  | "replace_with_marker"
  | "strip_to_text";

export interface DeadLink {
  /** Exact match z final_html — do surgical literal replace */
  matchedHtml: string;
  /** Raw href (np. "/zmieniały-się" lub "/zmiena%C5%82y-si%C4%99") */
  href: string;
  /** Decoded slug bez prefix/suffix "/" */
  decodedSlug: string;
  /** Anchor text (może zawierać <em>, <strong>) */
  anchor: string;
  type: DeadLinkType;

  /** Sugestia naprawy (jeśli detector zna kandydata). */
  suggestedAction?: DeadLinkAction;
  /** Dla `replace_href` — kanoniczny wp_post_url */
  suggestedHref?: string;
  /** Dla `replace_with_marker` — slug do marker'a (po slugify) */
  suggestedSlug?: string;
  /** Dla orphan — tytuł sugerowanego noda (do tooltip) */
  suggestedTitle?: string;
  /** Dla orphan — similarity score 0-1 */
  similarity?: number;
  /** Human-readable wyjaśnienie matchingu */
  reason: string;
}

export interface DeadLinkScan {
  sourceNodeId: string;
  sourceTitle: string;
  sourceSlug: string;
  wpPostId: number | null;
  deadLinks: DeadLink[];
  counts: {
    pending_target: number;
    slug_mismatch: number;
    orphan: number;
    total: number;
  };
}

interface NodeRow {
  id: string;
  slug: string;
  title: string;
  wp_post_url: string | null;
  wp_post_id: number | null;
  final_html: string | null;
  is_published: boolean;
}

/**
 * Bulk scan dla całej mapy. Zwraca wynik per published artykuł — TYLKO te z
 * ≥1 dead link (artykuły bez problemów pomijane).
 */
export async function scanDeadLinksForMap(
  mapId: string,
): Promise<DeadLinkScan[]> {
  const ctx = await loadMapContext(mapId);
  if (!ctx) return [];

  const results: DeadLinkScan[] = [];
  for (const article of ctx.publishedArticles) {
    const scan = analyzeArticle(article, ctx);
    if (scan.deadLinks.length > 0) results.push(scan);
  }
  return results;
}

/**
 * Per-artykuł scan (np. dla modalu naprawy 1 wybranego artykułu).
 * Zwraca scan ZAWSZE (nawet jeśli 0 dead links — counts pokazują 0).
 */
export async function scanDeadLinksForArticle(
  nodeId: string,
  mapId: string,
): Promise<DeadLinkScan | null> {
  const ctx = await loadMapContext(mapId);
  if (!ctx) return null;
  const article = ctx.publishedArticles.find((a) => a.id === nodeId);
  if (!article) return null;
  return analyzeArticle(article, ctx);
}

// =============================================
// Internal: loadMapContext + analyzeArticle
// =============================================

interface MapContext {
  /** Set żywych path'ów z wp_post_url (lowercase, bez slash) — liveness check */
  liveSlugSet: Set<string>;
  /** slug DB → node info */
  slugToNode: Map<
    string,
    { id: string; slug: string; wp_post_url: string | null; title: string }
  >;
  /** slugify(slug DB) → node info — dla detekcji "rozjazd po polskich znakach" */
  slugifiedToNode: Map<
    string,
    { id: string; slug: string; wp_post_url: string | null; title: string }
  >;
  /** Wszystkie published nody z wp_post_url — kandydaci dla orphan matcher */
  orphanCandidates: OrphanCandidate[];
  /** Published artykuły do skanowania */
  publishedArticles: NodeRow[];
}

async function loadMapContext(mapId: string): Promise<MapContext | null> {
  // 1. Pobierz wszystkie nody w mapie + ich content
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug, title")
    .eq("map_id", mapId);
  if (!nodes || nodes.length === 0) return null;

  const nodeIds = (nodes as Array<{ id: string }>).map((n) => n.id);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: contents } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, wp_post_url, wp_post_id, engine_stage, final_html")
    .in("node_id", nodeIds);

  const contentByNodeId = new Map<
    string,
    {
      wp_post_url: string | null;
      wp_post_id: number | null;
      engine_stage: string | null;
      final_html: string | null;
    }
  >();
  for (const c of (contents || []) as Array<{
    node_id: string;
    wp_post_url: string | null;
    wp_post_id: number | null;
    engine_stage: string | null;
    final_html: string | null;
  }>) {
    contentByNodeId.set(c.node_id, {
      wp_post_url: c.wp_post_url?.trim() || null,
      wp_post_id: c.wp_post_id ?? null,
      engine_stage: c.engine_stage ?? null,
      final_html: c.final_html ?? null,
    });
  }

  // Build slugToNode, slugifiedToNode, liveSlugSet, publishedArticles
  const slugToNode = new Map<
    string,
    { id: string; slug: string; wp_post_url: string | null; title: string }
  >();
  const slugifiedToNode = new Map<
    string,
    { id: string; slug: string; wp_post_url: string | null; title: string }
  >();
  const liveSlugSet = new Set<string>();
  const orphanCandidates: OrphanCandidate[] = [];
  const publishedArticles: NodeRow[] = [];

  for (const n of nodes as Array<{
    id: string;
    slug: string;
    title: string;
  }>) {
    const c = contentByNodeId.get(n.id);
    const info = {
      id: n.id,
      slug: n.slug,
      wp_post_url: c?.wp_post_url ?? null,
      title: n.title,
    };
    if (n.slug) {
      slugToNode.set(n.slug, info);
      const norm = slugify(n.slug);
      // Slugified map: pierwszy node wygrywa dla danego klucza (rzadkie kolizje)
      if (!slugifiedToNode.has(norm)) slugifiedToNode.set(norm, info);
    }
    if (c?.wp_post_url) {
      const livePath = extractPathFromWpUrl(c.wp_post_url);
      if (livePath) liveSlugSet.add(livePath);
      orphanCandidates.push({
        id: n.id,
        slug: n.slug,
        title: n.title,
        wp_post_url: c.wp_post_url,
      });
    }
    if (c?.engine_stage === "published" && c.final_html) {
      publishedArticles.push({
        id: n.id,
        slug: n.slug,
        title: n.title,
        wp_post_url: c.wp_post_url,
        wp_post_id: c.wp_post_id,
        final_html: c.final_html,
        is_published: true,
      });
    }
  }

  return {
    liveSlugSet,
    slugToNode,
    slugifiedToNode,
    orphanCandidates,
    publishedArticles,
  };
}

function analyzeArticle(article: NodeRow, ctx: MapContext): DeadLinkScan {
  const deadLinks: DeadLink[] = [];
  const seenMatched = new Set<string>(); // dedup per artykuł (ten sam <a> 2× → 1 fix)

  if (article.final_html) {
    const matches = article.final_html.matchAll(RELATIVE_LINK_RE);
    for (const m of matches) {
      const matchedHtml = m[0];
      const href = m[1];
      const anchor = m[2];
      if (seenMatched.has(matchedHtml)) continue;
      seenMatched.add(matchedHtml);

      // Decode + normalize path
      const decodedSlug = decodeHrefToSlug(href);
      if (!decodedSlug) continue;

      // STEP 0: liveness check po wp_post_url path
      if (ctx.liveSlugSet.has(decodedSlug)) {
        // Żywa strona pod tą ścieżką — link OK, skip
        continue;
      }

      // Self-link guard — nie klasyfikuj linku noda do siebie jako dead
      if (article.slug && decodedSlug === article.slug) continue;
      const normalizedSelf = slugify(article.slug || "");
      if (normalizedSelf && slugify(decodedSlug) === normalizedSelf) continue;

      const dead = classifyDead(matchedHtml, href, decodedSlug, anchor, ctx);
      if (dead) deadLinks.push(dead);
    }
  }

  const counts = {
    pending_target: 0,
    slug_mismatch: 0,
    orphan: 0,
    total: deadLinks.length,
  };
  for (const d of deadLinks) counts[d.type]++;

  return {
    sourceNodeId: article.id,
    sourceTitle: article.title,
    sourceSlug: article.slug,
    wpPostId: article.wp_post_id,
    deadLinks,
    counts,
  };
}

function classifyDead(
  matchedHtml: string,
  href: string,
  decodedSlug: string,
  anchor: string,
  ctx: MapContext,
): DeadLink | null {
  // STEP 1: Exact slug w DB
  const exact = ctx.slugToNode.get(decodedSlug);
  if (exact) {
    if (exact.wp_post_url) {
      // Slug istnieje w DB, ma wp_post_url, ALE link != livePath
      // → rozjazd (np. /zmieniały-się slug w DB vs /zmienialy-sie live URL)
      return {
        matchedHtml,
        href,
        decodedSlug,
        anchor,
        type: "slug_mismatch",
        suggestedAction: "replace_href",
        suggestedHref: exact.wp_post_url,
        suggestedTitle: exact.title,
        reason: `Slug DB istnieje, ale żywy URL to "${exact.wp_post_url}"`,
      };
    } else {
      return {
        matchedHtml,
        href,
        decodedSlug,
        anchor,
        type: "pending_target",
        suggestedAction: "replace_with_marker",
        suggestedSlug: decodedSlug,
        suggestedTitle: exact.title,
        reason: "Cel istnieje w mapie, czeka na publikację",
      };
    }
  }

  // STEP 2: slugify(decodedSlug) → match (case dla polskich znaków w href)
  const normalized = slugify(decodedSlug);
  if (normalized && normalized !== decodedSlug) {
    const slugified = ctx.slugifiedToNode.get(normalized);
    if (slugified) {
      if (slugified.wp_post_url) {
        return {
          matchedHtml,
          href,
          decodedSlug,
          anchor,
          type: "slug_mismatch",
          suggestedAction: "replace_href",
          suggestedHref: slugified.wp_post_url,
          suggestedTitle: slugified.title,
          reason: `Match po slugify (ASCII), żywy URL: ${slugified.wp_post_url}`,
        };
      } else {
        return {
          matchedHtml,
          href,
          decodedSlug,
          anchor,
          type: "pending_target",
          suggestedAction: "replace_with_marker",
          suggestedSlug: slugified.slug,
          suggestedTitle: slugified.title,
          reason: "Match po slugify, cel czeka na publikację",
        };
      }
    }
  }

  // STEP 3: orphan — fuzzy match
  const orphanMatch = findOrphanMatch(
    decodedSlug,
    anchor,
    ctx.orphanCandidates,
  );
  if (orphanMatch) {
    return {
      matchedHtml,
      href,
      decodedSlug,
      anchor,
      type: "orphan",
      suggestedAction: "replace_href",
      suggestedHref: orphanMatch.wpUrl,
      suggestedTitle: ctx.orphanCandidates.find(
        (c) => c.id === orphanMatch.nodeId,
      )?.title,
      similarity: orphanMatch.similarity,
      reason: `Pewne dopasowanie: ${orphanMatch.reason}`,
    };
  }

  // STEP 4: orphan bez pewnego match — strip do <span> (anchor neutralny)
  return {
    matchedHtml,
    href,
    decodedSlug,
    anchor,
    type: "orphan",
    suggestedAction: "strip_to_text",
    reason: "Brak pewnego dopasowania w mapie (similarity < 0.75)",
  };
}

// =============================================
// Pure helpers
// =============================================

/**
 * Wyciągnij path (bez protokołu/domeny/trailing slash) z wp_post_url.
 * "https://growthhacker.pl/jak-zbudowac/" → "jak-zbudowac"
 * "https://example.com/" → "" (root, edge case skip)
 */
export function extractPathFromWpUrl(wpUrl: string): string | null {
  if (!wpUrl) return null;
  try {
    const u = new URL(wpUrl);
    const path = u.pathname
      .toLowerCase()
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");
    return path || null;
  } catch {
    // Nie-URL, próba manual strip
    const stripped = wpUrl
      .toLowerCase()
      .replace(/^https?:\/\/[^/]+\//, "")
      .replace(/\/+$/, "")
      .split("?")[0]
      .split("#")[0];
    return stripped || null;
  }
}

/**
 * Decode href + strip "/" prefix/suffix + remove query/hash.
 * "/zmiena%C5%82y-si%C4%99/" → "zmieniały-się"
 * "/foo?x=1" → "foo"
 */
export function decodeHrefToSlug(href: string): string {
  if (!href) return "";
  let s = href;
  try {
    s = decodeURIComponent(s);
  } catch {
    /* malformed — leave raw */
  }
  // Order: strip query/hash FIRST, then trailing slash (żeby "/foo/?x=1" → "foo/" → "foo")
  s = s.split("?")[0].split("#")[0];
  return s.replace(/^\/+/, "").replace(/\/+$/, "");
}
